import { useState, useMemo } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import CalculatorShell from "@/components/CalculatorShell";
import Field from "@/components/Field";
import ResultStat from "@/components/ResultStat";
import { toNum, federalIncomeTax } from "@/lib/finance";
import { fmtUSD, fmtPct } from "@/lib/format";
import { STATE_TAX_LIST, getStateTax } from "@/data/stateTax";
import { breadcrumbJsonLd } from "@/lib/seo";
import Seo from "@/components/Seo";
import EditorBadge from "@/components/EditorBadge";
import { Wallet } from "lucide-react";

// Programmatic SEO: /calculator/take-home/:state
// 50 indexable pages — companion to GeoIncomeTaxPage but focused on
// paycheck breakdown (annual, monthly, biweekly, weekly, hourly).
// Targets: "[state] take home pay calculator", "[state] paycheck calculator".
export default function GeoTakeHomePage() {
    const { state: code } = useParams();
    const state = getStateTax(code || "");

    const [gross, setGross] = useState(75000);
    const [filing, setFiling] = useState("single");
    const [pretax, setPretax] = useState(0);

    const result = useMemo(() => {
        if (!state) return null;
        const g = toNum(gross);
        const pre = Math.min(toNum(pretax), g);
        const taxableForTax = Math.max(0, g - pre);
        const fed = federalIncomeTax(taxableForTax, filing);
        let stateTax = 0;
        if (!state.noTax) {
            const std = state.std?.[filing] ?? 0;
            const stTaxable = Math.max(0, taxableForTax - std);
            stateTax = stTaxable * (state.flatRate / 100);
        }
        // FICA on full gross (pre-tax retirement reduces income tax but
        // NOT FICA for traditional 401(k); HSA/health pre-tax does both —
        // we use the conservative middle ground here).
        const ficaWageBase = Math.min(g, 176100); // 2025 SS wage base
        const ssTax = ficaWageBase * 0.062;
        const medicareTax = g * 0.0145;
        const fica = ssTax + medicareTax;
        const totalTax = fed.tax + stateTax + fica;
        const takeHome = g - totalTax - pre;
        return {
            fed: fed.tax,
            stateTax,
            fica,
            ssTax,
            medicareTax,
            pre,
            totalTax,
            takeHome,
            monthly: takeHome / 12,
            biweekly: takeHome / 26,
            weekly: takeHome / 52,
            hourly: takeHome / 2080,
            effectiveTotal: g > 0 ? (totalTax / g) * 100 : 0,
        };
    }, [gross, filing, pretax, state]);

    if (!state) return <Navigate to="/calculator/salary" replace />;

    const stateLower = (code || "").toLowerCase();
    const title = `${state.name} Take-Home Pay Calculator — 2025 Paycheck`;
    const description = state.noTax
        ? `Calculate your ${state.name} paycheck — federal + FICA only (no state income tax). Annual, monthly, biweekly, weekly, hourly breakdowns.`
        : `Calculate ${state.name} take-home pay after federal, state (${state.flatRate}%), and FICA tax. Annual, monthly, biweekly, weekly, hourly breakdowns. 2025 rates.`;

    const jsonLd = [
        {
            "@context": "https://schema.org",
            "@type": "WebApplication",
            name: title,
            applicationCategory: "FinanceApplication",
            operatingSystem: "Any",
            description,
            areaServed: { "@type": "State", name: state.name, address: { "@type": "PostalAddress", addressRegion: state.upperCode || (code || "").toUpperCase(), addressCountry: "US" } },
            offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
        },
        breadcrumbJsonLd([
            { name: "Home", path: "/" },
            { name: "Salary", path: "/calculator/salary" },
            { name: `${state.name} Take-Home`, path: `/calculator/take-home/${stateLower}` },
        ]),
    ];

    return (
        <>
            <Seo title={`${title} | FinCalc`} description={description} canonical={`/calculator/take-home/${stateLower}`} jsonLd={jsonLd} ogSlug="salary" />

            <div className="container-editorial pt-8 pb-2">
                <nav className="flex items-center gap-2 text-xs text-muted-foreground" aria-label="Breadcrumb">
                    <Link to="/" className="hover:text-primary">Home</Link>
                    <span>/</span>
                    <Link to="/calculator/salary" className="hover:text-primary">Salary</Link>
                    <span>/</span>
                    <span className="text-primary">{state.name} take-home</span>
                </nav>
            </div>

            <section className="container-editorial pt-4 pb-6 anim-fade-up">
                <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-accent mb-3">
                    <Wallet className="w-3.5 h-3.5" strokeWidth={1.5} aria-hidden="true" />
                    {state.name} · Paycheck breakdown
                </div>
                <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-primary leading-[1.05] tracking-tight text-balance">
                    {state.name} Take-Home Pay Calculator
                </h1>
                <p className="mt-4 max-w-2xl text-lg text-foreground/80 leading-relaxed">
                    See exactly what hits your bank account. Federal tax + {state.noTax ? "no state tax" : `${state.name} state tax (${state.topRate}% top rate)`} + FICA, broken down annual / monthly / biweekly / weekly / hourly.
                </p>
                <div className="mt-5"><EditorBadge /></div>
            </section>

            <CalculatorShell
                slug="salary"
                hideHeader
                suppressSeo
                explainer={
                    <>
                        <h2>How your {state.name} paycheck is built</h2>
                        <p>
                            Every paycheck, three taxes come off the top before anything else:
                            federal income tax (graduated brackets), <strong>FICA</strong>
                            {" "}(7.65%: 6.2% Social Security + 1.45% Medicare),
                            {state.noTax
                                ? <> and that's it for <strong>{state.name}</strong> — there is no state income tax.</>
                                : <> and <strong>{state.name}</strong> state income tax at roughly {state.flatRate}% effective.</>}
                        </p>
                        <p>{state.note}</p>
                        <h3>Pre-tax deductions matter</h3>
                        <p>
                            Traditional 401(k), HSA, and pre-tax health premiums lower your
                            taxable income — meaning you pay less federal and (in most states)
                            less state tax. Use the "Pre-tax deductions" field above to model
                            this. Roth 401(k) and Roth IRA contributions do <em>not</em> reduce
                            current taxable income.
                        </p>
                        <h3>Why the hourly figure assumes 2,080 hours</h3>
                        <p>
                            2,080 hours = 40 hours × 52 weeks. A typical full-time year. If you
                            actually work overtime or part-time, your effective hourly
                            take-home is different — see our <Link to="/calculator/overtime-tips">Overtime &amp; Tips Tax Calculator</Link> for
                            the 2025 OBBBA "no tax on overtime" math.
                        </p>
                        <h3>What this calculator <em>doesn't</em> include</h3>
                        <ul>
                            <li>Local/city wage tax (NYC, Philly, Detroit, etc.) — add 1–4% if applicable</li>
                            <li>Itemized deductions or tax credits beyond standard deduction</li>
                            <li>State disability insurance (CA SDI, NY/NJ TDI, etc.)</li>
                            <li>Additional 0.9% Medicare surtax above $200K single / $250K joint</li>
                        </ul>
                        <p>
                            For an exact figure, use the IRS Tax Withholding Estimator or
                            consult a CPA. This calculator is excellent for offer-comparison,
                            relocation math, and "should I take the new job?" decisions.
                        </p>
                    </>
                }
            >
                <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
                    <div className="lg:col-span-3 bg-card rounded-xl border border-border p-6 sm:p-8 space-y-5">
                        <Field label="Gross annual salary" prefix="$" value={gross} onChange={setGross} dataTestId="th-input-gross" />
                        <Field label="Pre-tax deductions (401k, HSA)" prefix="$" value={pretax} onChange={setPretax} dataTestId="th-input-pretax" />
                        <div>
                            <label className="block font-sans text-[11px] uppercase tracking-[0.18em] text-primary/80 font-medium mb-1.5">Filing status</label>
                            <div className="inline-flex rounded-md border border-border overflow-hidden" role="tablist">
                                <button role="tab" aria-selected={filing === "single"} onClick={() => setFiling("single")} data-testid="th-filing-single" className={`px-4 h-11 text-sm transition-colors ${filing === "single" ? "bg-primary text-primary-foreground" : "bg-background text-primary hover:bg-muted"}`}>Single</button>
                                <button role="tab" aria-selected={filing === "married"} onClick={() => setFiling("married")} data-testid="th-filing-married" className={`px-4 h-11 text-sm transition-colors border-l border-border ${filing === "married" ? "bg-primary text-primary-foreground" : "bg-background text-primary hover:bg-muted"}`}>Married joint</button>
                            </div>
                        </div>
                        <div className="pt-3 space-y-1.5 text-xs text-muted-foreground font-mono tabular border-t border-border">
                            <div className="flex justify-between"><span>Federal income tax</span><span>{fmtUSD(result.fed, { decimals: 0 })}</span></div>
                            <div className="flex justify-between"><span>{state.name} state tax</span><span>{state.noTax ? "$0 (no state tax)" : fmtUSD(result.stateTax, { decimals: 0 })}</span></div>
                            <div className="flex justify-between"><span>Social Security (6.2%)</span><span>{fmtUSD(result.ssTax, { decimals: 0 })}</span></div>
                            <div className="flex justify-between"><span>Medicare (1.45%)</span><span>{fmtUSD(result.medicareTax, { decimals: 0 })}</span></div>
                            {result.pre > 0 && <div className="flex justify-between"><span>Pre-tax deductions</span><span>{fmtUSD(result.pre, { decimals: 0 })}</span></div>}
                        </div>
                    </div>
                    <div className="lg:col-span-2">
                        <ResultStat
                            label="Annual take-home"
                            value={result.takeHome}
                            format={(n) => fmtUSD(n, { decimals: 0 })}
                            secondary={[
                                { label: "Per month", value: result.monthly, format: (n) => fmtUSD(n, { decimals: 0 }), testId: "th-result-monthly" },
                                { label: "Per biweekly", value: result.biweekly, format: (n) => fmtUSD(n, { decimals: 0 }), testId: "th-result-biweekly" },
                                { label: "Per week", value: result.weekly, format: (n) => fmtUSD(n, { decimals: 0 }), testId: "th-result-weekly" },
                                { label: "Per hour (2,080 hr/yr)", value: result.hourly, format: (n) => fmtUSD(n, { decimals: 2 }), testId: "th-result-hourly" },
                                { label: "Total tax burden", value: result.effectiveTotal, format: (n) => fmtPct(n, 1), testId: "th-result-effective" },
                            ]}
                        />
                    </div>
                </div>

                {/* Cross-link to compare other states */}
                <div className="mt-10 bg-card border border-border rounded-xl p-6">
                    <div className="text-[11px] uppercase tracking-[0.2em] text-accent">Compare states</div>
                    <h3 className="font-serif text-2xl text-primary mt-1 mb-4">
                        How does {state.name} stack up?
                    </h3>
                    <p className="text-sm text-muted-foreground mb-4 max-w-2xl">
                        Considering a move? Run the same salary through another state's calculator.
                    </p>
                    <div className="flex flex-wrap gap-2">
                        {STATE_TAX_LIST.filter((s) => s.code !== stateLower).slice(0, 14).map((s) => (
                            <Link
                                key={s.code}
                                to={`/calculator/take-home/${s.code}`}
                                className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                                    s.noTax
                                        ? "border-accent/40 bg-accent/5 text-accent hover:border-accent"
                                        : "border-border text-primary hover:border-primary/40"
                                }`}
                            >
                                {s.name}{s.noTax ? " (no tax)" : ""}
                            </Link>
                        ))}
                    </div>
                    <div className="mt-4 text-xs text-muted-foreground">
                        Want pure tax math (no FICA / paycheck split)? See the{" "}
                        <Link to={`/calculator/income-tax/${stateLower}`} className="text-accent hover:underline">
                            {state.name} income-tax calculator →
                        </Link>
                    </div>
                </div>
            </CalculatorShell>
        </>
    );
}
